import React, { useState, useEffect } from 'react';
import { 
  Notification, 
  NotificationType, 
  TeamMember 
} from '../../../types/teamCollaboration';
import { tone, toneForPriority } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

interface NotificationSystemProps {
  notifications: Notification[];
  currentUser: TeamMember;
  onMarkAsRead: (notificationId: string) => void;
  onMarkAllAsRead: () => void;
  onDismiss: (notificationId: string) => void;
  onAction: (notificationId: string, actionId: string) => void;
}

const NOTIFICATION_ICONS: Record<NotificationType, string> = {
  test_created: '🧪',
  test_completed: '✅',
  approval_required: '⏳',
  approval_granted: '✅',
  approval_rejected: '❌',
  agent_decision: '🤖',
  performance_alert: '⚠️',
  comment_added: '💬',
  mention: '👤',
  deadline_approaching: '⏰'
};

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  test_created: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  test_completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  approval_required: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  approval_granted: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  approval_rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  agent_decision: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
  performance_alert: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
  comment_added: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  mention: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
  deadline_approaching: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
};

export const NotificationSystem: React.FC<NotificationSystemProps> = ({
  notifications,
  currentUser: _currentUser,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onAction
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | NotificationType>('all');

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.isRead;
    return notification.type === filter;
  });

  const sortedNotifications = filteredNotifications.sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
  };

  const handleActionClick = (notificationId: string, actionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onAction(notificationId, actionId);
  };

  const getTimeAgo = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const getPriorityColor = (priority) => tone(toneForPriority(priority)).chip;

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-content-secondary hover:text-content focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
      >
        <div className="w-6 h-6">
          🔔
        </div>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-surface rounded-lg shadow-lg border border-line z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-line">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-content">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllAsRead}
                  className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex space-x-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  filter === 'all' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' : 'text-content-secondary hover:text-content'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  filter === 'unread' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' : 'text-content-secondary hover:text-content'
                }`}
              >
                Unread ({unreadCount})
              </button>
              <SelectField
                value={filter === 'all' || filter === 'unread' ? '' : filter}
                onChange={(e) => setFilter(e.target.value as NotificationType || 'all')}
                className="px-2 py-1 border border-line-strong rounded text-sm"
              >
                <option value="">Type</option>
                <option value="approval_required">Approvals</option>
                <option value="test_completed">Tests</option>
                <option value="agent_decision">Agents</option>
                <option value="comment_added">Comments</option>
                <option value="mention">Mentions</option>
              </SelectField>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {sortedNotifications.length === 0 ? (
              <div className="p-8 text-center text-content-muted">
                <div className="text-4xl mb-2">🔕</div>
                <p>No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {sortedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer border-l-4 ${getPriorityColor(notification.priority)} ${
                      !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <span className="text-lg">
                          {NOTIFICATION_ICONS[notification.type]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-content truncate">
                            {notification.title}
                          </p>
                          <div className="flex items-center space-x-2">
                            {!notification.isRead && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            )}
                            <span className="text-xs text-content-muted">
                              {getTimeAgo(notification.timestamp)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-content-secondary mb-2">
                          {notification.message}
                        </p>

                        {/* Notification Actions */}
                        {notification.actions.length > 0 && (
                          <div className="flex space-x-2">
                            {notification.actions.map((action) => (
                              <button
                                key={action.id}
                                onClick={(e) => handleActionClick(notification.id, action.id, e)}
                                className={`px-3 py-1 rounded text-xs font-medium ${
                                  action.primary
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                                }`}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Type Badge */}
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            NOTIFICATION_COLORS[notification.type]
                          }`}>
                            {notification.type.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Dismiss Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDismiss(notification.id);
                        }}
                        className="flex-shrink-0 text-content-subtle hover:text-content-secondary"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {sortedNotifications.length > 0 && (
            <div className="p-3 border-t border-line bg-gray-50 dark:bg-dark-700">
              <button
                onClick={() => setShowDropdown(false)}
                className="w-full text-center text-sm text-content-secondary hover:text-content"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

// Toast Notification Component for real-time notifications
interface ToastNotificationProps {
  notification: Notification;
  onDismiss: () => void;
  onAction: (actionId: string) => void;
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  notification,
  onDismiss,
  onAction
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const getBgColor = (priority: string): string => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm w-full ${getBgColor(notification.priority)} text-white rounded-lg shadow-lg`}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <span className="text-lg">
              {NOTIFICATION_ICONS[notification.type]}
            </span>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium">
              {notification.title}
            </p>
            <p className="text-sm opacity-90 mt-1">
              {notification.message}
            </p>
            {notification.actions.length > 0 && (
              <div className="mt-3 flex space-x-2">
                {notification.actions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => onAction(action.id)}
                    className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs font-medium"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onDismiss}
            className="flex-shrink-0 ml-4 text-white hover:text-gray-200"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSystem;